
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Star, StarHalf } from 'lucide-react';
import { toast } from 'sonner';

interface Rating {
  userId: string;
  recipeId: string;
  flavor: number; // 1-5
  value: number; // 1-5
  ease: number; // 1-5
  time: number; // 1-5
  overall: number; // 1-5
  created_at: string;
  user_name?: string;
}

interface RecipeRatingsProps {
  recipeId: string;
}

// Helper to get ratings from local storage
const getLocalRatings = (recipeId: string) => {
  const key = `recipe-ratings-${recipeId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : [];
};

// Helper to save ratings to local storage
const saveLocalRating = (recipeId: string, rating: Rating) => {
  const key = `recipe-ratings-${recipeId}`;
  const existingRatings = getLocalRatings(recipeId);
  
  // Check if user already rated this recipe
  const userIndex = existingRatings.findIndex(
    (r: Rating) => r.userId === rating.userId
  );
  
  let updatedRatings;
  if (userIndex >= 0) {
    // Update existing rating
    updatedRatings = [...existingRatings];
    updatedRatings[userIndex] = rating;
  } else {
    // Add new rating
    updatedRatings = [...existingRatings, rating];
  }
  
  localStorage.setItem(key, JSON.stringify(updatedRatings));
  return updatedRatings;
};

const RatingStars = ({ 
  value, 
  onChange, 
  readOnly = false 
}: { 
  value: number; 
  onChange?: (value: number) => void;
  readOnly?: boolean;
}) => {
  const [hoverValue, setHoverValue] = useState(0);
  
  const handleClick = (starValue: number) => {
    if (!readOnly && onChange) {
      onChange(starValue);
    }
  };

  const renderStars = () => {
    const stars = [];
    const displayValue = readOnly ? value : hoverValue || value;
    
    for (let i = 1; i <= 5; i++) {
      const filled = i <= displayValue;
      const halfFilled = !filled && i - 0.5 <= displayValue;
      
      stars.push(
        <button
          key={i}
          type="button"
          className={`text-${filled || halfFilled ? "amber-400" : "gray-300"} 
                     ${!readOnly ? "hover:text-amber-400 cursor-pointer" : ""}
                     focus:outline-none`}
          onClick={() => handleClick(i)}
          onMouseEnter={() => !readOnly && setHoverValue(i)}
          onMouseLeave={() => !readOnly && setHoverValue(0)}
          disabled={readOnly}
        >
          {halfFilled ? (
            <StarHalf size={readOnly ? 18 : 24} />
          ) : (
            <Star size={readOnly ? 18 : 24} className={filled ? "fill-current" : ""} />
          )}
        </button>
      );
    }
    
    return stars;
  };
  
  return (
    <div className="flex items-center space-x-1">
      {renderStars()}
    </div>
  );
};

const RecipeRatings = ({ recipeId }: RecipeRatingsProps) => {
  const { user } = useAuth();
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [userRating, setUserRating] = useState<Rating | null>(null);
  const [allRatings, setAllRatings] = useState<Rating[]>([]);
  const [averageRatings, setAverageRatings] = useState({
    flavor: 0,
    value: 0,
    ease: 0,
    time: 0,
    overall: 0,
    total: 0
  });
  
  // Initialize rating form
  const [formRating, setFormRating] = useState({
    flavor: 0,
    value: 0, 
    ease: 0,
    time: 0,
    overall: 0
  });
  
  // Load ratings on mount
  useEffect(() => {
    const loadRatings = () => {
      const ratings = getLocalRatings(recipeId);
      setAllRatings(ratings);
      
      // Check if current user has already rated
      if (user) {
        const userRating = ratings.find((r: Rating) => r.userId === user.id);
        if (userRating) {
          setUserRating(userRating);
          setFormRating({
            flavor: userRating.flavor,
            value: userRating.value,
            ease: userRating.ease,
            time: userRating.time,
            overall: userRating.overall
          });
        }
      }
      
      // Calculate average ratings
      if (ratings.length > 0) {
        const sumRatings = ratings.reduce((acc: any, curr: Rating) => {
          return {
            flavor: acc.flavor + curr.flavor,
            value: acc.value + curr.value,
            ease: acc.ease + curr.ease,
            time: acc.time + curr.time,
            overall: acc.overall + curr.overall
          };
        }, { flavor: 0, value: 0, ease: 0, time: 0, overall: 0 });
        
        setAverageRatings({
          flavor: sumRatings.flavor / ratings.length,
          value: sumRatings.value / ratings.length,
          ease: sumRatings.ease / ratings.length,
          time: sumRatings.time / ratings.length,
          overall: sumRatings.overall / ratings.length,
          total: ratings.length
        });
      }
    };
    
    loadRatings();
  }, [recipeId, user]);
  
  const handleRatingSubmit = () => {
    if (!user) {
      toast.error("Debes iniciar sesión para valorar esta receta");
      return;
    }
    
    // Validate that all ratings are set
    if (
      formRating.flavor === 0 || 
      formRating.value === 0 || 
      formRating.ease === 0 || 
      formRating.time === 0 || 
      formRating.overall === 0
    ) {
      toast.error("Por favor, valora todos los aspectos");
      return;
    }
    
    // Create rating object
    const newRating: Rating = {
      userId: user.id,
      user_name: user.name,
      recipeId,
      flavor: formRating.flavor,
      value: formRating.value,
      ease: formRating.ease,
      time: formRating.time,
      overall: formRating.overall,
      created_at: new Date().toISOString()
    };
    
    // Save to local storage
    const updatedRatings = saveLocalRating(recipeId, newRating);
    
    // Update state
    setAllRatings(updatedRatings);
    setUserRating(newRating);
    
    // Recalculate averages
    if (updatedRatings.length > 0) {
      const sumRatings = updatedRatings.reduce((acc: any, curr: Rating) => {
        return {
          flavor: acc.flavor + curr.flavor,
          value: acc.value + curr.value,
          ease: acc.ease + curr.ease,
          time: acc.time + curr.time,
          overall: acc.overall + curr.overall
        };
      }, { flavor: 0, value: 0, ease: 0, time: 0, overall: 0 });
      
      setAverageRatings({
        flavor: sumRatings.flavor / updatedRatings.length,
        value: sumRatings.value / updatedRatings.length,
        ease: sumRatings.ease / updatedRatings.length,
        time: sumRatings.time / updatedRatings.length,
        overall: sumRatings.overall / updatedRatings.length,
        total: updatedRatings.length
      });
    }
    
    // Determine if this is a new rating or an update
    // Check if the user already had a rating before this submission
    const isNewRating = !allRatings.some(rating => rating.userId === user.id);
    
    console.log("[RATINGS CHALLENGE] ★★★ SUBMIT CLICKED ★★★");
    console.log("[RATINGS CHALLENGE] Is this a new rating?", isNewRating, "User:", user.id);
    console.log("[RATINGS CHALLENGE] Recipe ID:", recipeId);
    
    // Dispatch rating-added event for the challenge tracking
    try {
      const ratingAddedEvent = new CustomEvent('recipe-rated', {
        detail: {
          userId: user.id,
          recipeId,
          rating: newRating,
          isNew: isNewRating
        }
      });
      window.dispatchEvent(ratingAddedEvent);
      console.log("[RATINGS CHALLENGE] Dispatched recipe-rated event", ratingAddedEvent.detail);
      
      // Force track the rating for the challenge regardless of the event
      try {
        // Dynamic import to avoid circular dependencies
        import('@/integrations/supabase/client').then(({ trackRatingForChallenge }) => {
          trackRatingForChallenge(user.id, {
            recipeId,
            timestamp: new Date().toISOString()
          }).then(() => {
            console.log("[RATINGS CHALLENGE] Successfully tracked rating directly");
          });
        }).catch(err => {
          console.error("[RATINGS CHALLENGE] Error importing trackRatingForChallenge:", err);
        });
      } catch (trackError) {
        console.error("[RATINGS CHALLENGE] Error calling trackRatingForChallenge directly:", trackError);
      }
    } catch (e) {
      console.error("[RATINGS CHALLENGE] Error dispatching recipe-rated event:", e);
    }
    
    // Hide form
    setShowRatingForm(false);
    toast.success("¡Gracias por tu valoración!");
  };
  
  const editRating = () => {
    if (userRating) {
      setFormRating({
        flavor: userRating.flavor,
        value: userRating.value,
        ease: userRating.ease,
        time: userRating.time,
        overall: userRating.overall
      });
    }
    setShowRatingForm(true);
  };
  
  return (
    <div className="mt-16">
      <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-200">Valoraciones</h2>
      
      {!showRatingForm ? (
        <div>
          {/* Summary of ratings */}
          <div className="flex flex-wrap justify-between mb-8">
            <div className="w-full md:w-1/2 lg:w-auto mb-4 md:mb-0 flex flex-col items-center">
              <div className="text-4xl font-bold text-amber-400 flex items-center">
                {averageRatings.overall.toFixed(1)}
                <Star size={32} className="ml-1 fill-current text-amber-400" />
              </div>
              <div className="text-gray-500 text-sm">
                {averageRatings.total} {averageRatings.total === 1 ? 'valoración' : 'valoraciones'}
              </div>
            </div>
            
            <div className="w-full md:w-1/2 lg:flex-1 grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 mb-1">Sabor</span>
                <div className="flex items-center">
                  <RatingStars value={averageRatings.flavor} readOnly />
                  <span className="ml-2 text-sm font-medium">{averageRatings.flavor.toFixed(1)}</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 mb-1">Calidad/precio</span>
                <div className="flex items-center">
                  <RatingStars value={averageRatings.value} readOnly />
                  <span className="ml-2 text-sm font-medium">{averageRatings.value.toFixed(1)}</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 mb-1">Facilidad</span>
                <div className="flex items-center">
                  <RatingStars value={averageRatings.ease} readOnly />
                  <span className="ml-2 text-sm font-medium">{averageRatings.ease.toFixed(1)}</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 mb-1">Tiempo</span>
                <div className="flex items-center">
                  <RatingStars value={averageRatings.time} readOnly />
                  <span className="ml-2 text-sm font-medium">{averageRatings.time.toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Rate this recipe button */}
          {user && (
            <div className="flex justify-center mb-8">
              <Button
                onClick={userRating ? editRating : () => setShowRatingForm(true)}
                className="bg-vegana-primary hover:bg-vegana-secondary"
              >
                {userRating ? 'Editar mi valoración' : 'Valorar esta receta'}
              </Button>
            </div>
          )}
          
          {!user && (
            <div className="p-4 mb-6 bg-gray-50 rounded-md text-center">
              <p className="text-gray-600 mb-2">Inicia sesión para valorar esta receta</p>
              <Button 
                variant="outline" 
                className="text-vegana-primary border-vegana-primary hover:bg-vegana-primary/10"
                onClick={() => window.location.href = '/login'}
              >
                Iniciar Sesión
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Rating Form */
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h3 className="text-lg font-semibold mb-6">Tu valoración</h3>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sabor
                </label>
                <RatingStars 
                  value={formRating.flavor} 
                  onChange={(value) => setFormRating({...formRating, flavor: value})} 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Calidad/precio
                </label>
                <RatingStars 
                  value={formRating.value} 
                  onChange={(value) => setFormRating({...formRating, value: value})} 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facilidad
                </label>
                <RatingStars 
                  value={formRating.ease} 
                  onChange={(value) => setFormRating({...formRating, ease: value})} 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tiempo
                </label>
                <RatingStars 
                  value={formRating.time} 
                  onChange={(value) => setFormRating({...formRating, time: value})} 
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valoración general
              </label>
              <RatingStars 
                value={formRating.overall} 
                onChange={(value) => setFormRating({...formRating, overall: value})} 
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowRatingForm(false)}
              >
                Cancelar
              </Button>
              <Button 
                className="bg-vegana-primary hover:bg-vegana-secondary"
                onClick={handleRatingSubmit}
              >
                {userRating ? 'Actualizar valoración' : 'Enviar valoración'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeRatings;
